import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICE_TYPES, SHIPMENT_STATUSES } from "@/data/services";
import { EmptyState, adminInputCls } from "@/components/admin/AdminBits";

export function ShipmentsPanel({ shipments, onStatusChange, onDelete, onCreated }) {
  const [showNew, setShowNew] = useState(false);

  return (
    <div data-testid="shipments-panel">
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-shipment-btn"
          className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] text-white px-5 py-2.5 font-bold uppercase tracking-wider text-xs"
        >
          <Plus className="h-4 w-4" /> New Shipment
        </button>
      </div>

      {showNew && <NewShipmentForm onClose={() => setShowNew(false)} onCreated={onCreated} />}

      <div className="border border-slate-200">
        {shipments.length === 0 ? (
          <EmptyState label="No shipments yet. Create the first one." />
        ) : (
          <ShipmentsTable shipments={shipments} onStatusChange={onStatusChange} onDelete={onDelete} />
        )}
      </div>
    </div>
  );
}

function ShipmentsTable({ shipments, onStatusChange, onDelete }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-slate-500 border-b border-slate-200">
            <th className="px-4 py-3">Tracking #</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Route</th>
            <th className="px-4 py-3">Service</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {shipments.map((s) => (
            <tr key={s.id} data-testid={`shipment-row-${s.id}`} className="border-b border-slate-200 hover:bg-white">
              <td className="px-4 py-4">
                <div className="flex items-center gap-2 font-heading font-bold text-primary-brand">
                  {s.tracking_number}
                  <button
                    onClick={() => { navigator.clipboard.writeText(s.tracking_number); toast.success("Copied"); }}
                    className="text-slate-500 hover:text-slate-900"
                    data-testid={`shipment-copy-${s.id}`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-1">{new Date(s.created_at).toLocaleDateString()}</div>
              </td>
              <td className="px-4 py-4 text-slate-900 font-heading font-bold">{s.customer_name}</td>
              <td className="px-4 py-4 text-slate-700 text-xs">
                <div>{s.origin}</div>
                <div className="text-slate-400">→ {s.destination}</div>
              </td>
              <td className="px-4 py-4 text-slate-700 text-xs">{s.service_type}</td>
              <td className="px-4 py-4">
                <select
                  value={s.current_status}
                  onChange={(e) => onStatusChange(s.id, e.target.value)}
                  data-testid={`shipment-status-${s.id}`}
                  className="bg-transparent border border-primary-brand/70 text-primary-brand px-2 py-1.5 text-xs uppercase tracking-wider"
                >
                  {SHIPMENT_STATUSES.map((opt) => <option key={opt.key} value={opt.key} className="bg-brand-bg">{opt.label}</option>)}
                </select>
              </td>
              <td className="px-4 py-4 text-right">
                <button onClick={() => onDelete(s.id)} data-testid={`shipment-delete-${s.id}`} className="text-slate-500 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewShipmentForm({ onClose, onCreated }) {
  const [f, setF] = useState({
    customer_name: "", customer_email: "", origin: "", destination: "",
    service_type: SERVICE_TYPES[0], weight: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/shipments", f);
      toast.success(`Created ${data.tracking_number}`);
      onCreated(data);
      onClose();
    } catch (error) {
      console.error("Create shipment failed:", error);
      toast.error("Failed to create shipment");
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} data-testid="new-shipment-form" className="border border-slate-200 bg-white p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Customer name *</div>
        <input required value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={adminInputCls} data-testid="new-ship-customer" />
      </label>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Customer email (links to portal)</div>
        <input type="email" value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} placeholder="client@example.com" className={adminInputCls} data-testid="new-ship-email" />
      </label>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Service type</div>
        <select value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} className={adminInputCls} data-testid="new-ship-service">
          {SERVICE_TYPES.map((s) => <option key={s} value={s} className="bg-brand-bg">{s}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Origin *</div>
        <input required value={f.origin} onChange={(e) => setF({ ...f, origin: e.target.value })} className={adminInputCls} data-testid="new-ship-origin" />
      </label>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Destination *</div>
        <input required value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} className={adminInputCls} data-testid="new-ship-dest" />
      </label>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Weight / size</div>
        <input value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} className={adminInputCls} data-testid="new-ship-weight" />
      </label>
      <label className="block md:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Notes</div>
        <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={adminInputCls + " resize-none"} data-testid="new-ship-notes" />
      </label>
      <div className="md:col-span-2 flex justify-end gap-3">
        <button type="button" onClick={onClose} className="border border-slate-200 hover:border-slate-900 text-slate-600 hover:text-slate-900 px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
          Cancel
        </button>
        <button type="submit" disabled={loading} data-testid="new-ship-submit" className="bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
          {loading ? "Creating…" : "Create Shipment"}
        </button>
      </div>
    </form>
  );
}
